import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {useRef} from 'react';
import type {PDFPageProxy} from 'pdfjs-dist';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Text, TextSize} from '@Components/text';
import type {PdfCanvasProps} from './pdf-canvas.types';
import {PDF_CANVAS_MAX_PIXEL_RATIO, PdfCanvasStatus} from './pdf-canvas.types';
import styles from './pdf-canvas.module.scss';

function appendPageCanvas(container: HTMLDivElement, page: PDFPageProxy, pixelRatio: number): Promise<void> | null {
  const viewport = page.getViewport({scale: pixelRatio});
  const canvas = window.document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (context === null) {
    return null;
  }

  canvas.className = styles.page;
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  canvas.style.width = `${viewport.width / pixelRatio}px`;
  container.appendChild(canvas);

  return page.render({canvas, canvasContext: context, viewport}).promise;
}

export function PdfCanvas({url, className = ''}: PdfCanvasProps): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<PdfCanvasStatus>(PdfCanvasStatus.LOADING);

  useEffect(() => {
    const container = containerRef.current;

    if (container === null || url === '') {
      return;
    }

    let isCancelled = false;
    let destroyLoadingTask: (() => void) | null = null;

    setStatus(PdfCanvasStatus.LOADING);

    const renderPages = async (): Promise<void> => {
      const pdfjsLib = await import('pdfjs-dist');

      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

      const loadingTask = pdfjsLib.getDocument({url});
      destroyLoadingTask = (): void => {
        void loadingTask.destroy();
      };

      const document = await loadingTask.promise;
      const pageNumbers = Array.from(Array(document.numPages).keys()).map((index) => index + 1);
      const pages = await Promise.all(pageNumbers.map(async (pageNumber) => document.getPage(pageNumber)));

      if (isCancelled) {
        return;
      }

      container.replaceChildren();

      const pixelRatio = Math.min(window.devicePixelRatio, PDF_CANVAS_MAX_PIXEL_RATIO);
      const renderTasks = pages.map((page) => appendPageCanvas(container, page, pixelRatio)).filter((task): task is Promise<void> => task !== null);

      await Promise.all(renderTasks);
    };

    renderPages()
      .then((): void => {
        if (isCancelled) {
          return;
        }

        setStatus(PdfCanvasStatus.READY);
      })
      .catch((e: unknown): void => {
        if (isCancelled) {
          return;
        }

        window.PMW.log(`PdfCanvas failed to render ${url}: ${String(e)}`);
        setStatus(PdfCanvasStatus.ERROR);
      });

    return (): void => {
      isCancelled = true;
      destroyLoadingTask?.();
    };
  }, [url]);

  const renderStatusOverlay = (): ReactElement | null => {
    if (status === PdfCanvasStatus.LOADING) {
      return (
        <div className={styles.overlay}>
          <CircularProgressLoader classes="_unmargin" size={LOADER_SIZE.LARGE} color={LOADER_COLOR.WHITE} />
        </div>
      );
    }

    if (status === PdfCanvasStatus.ERROR) {
      return (
        <div className={styles.overlay}>
          <Text val={window.i18next.t('pmwjs_something_went_wrong')} size={TextSize.MD} className={styles.errorText} />
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`${styles.root} ${className}`}>
      <div ref={containerRef} className={styles.pages} />
      {renderStatusOverlay()}
    </div>
  );
}
